/**
 * Command for changing the size of a dimension: text height, the arrows
 * following through DIMSCALE, and possibly its font.
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle } from "../core/oda";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";
import { selectionService } from "../selection";
import {
  applyDimensionSize,
  type DimensionSizeState,
} from "../dimensions/creation";

export interface SetDimensionSizeParams {
  /** Handle of the dimension entity */
  entityHandle: string;
  from: DimensionSizeState;
  to: DimensionSizeState;
  /** Text heights before and after, for the activity log. */
  fromHeight: number;
  toHeight: number;
}

export class SetDimensionSizeCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: SetDimensionSizeParams;
  private executed = false;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: SetDimensionSizeParams) {
    this.viewer = viewer;
    this.params = params;
  }

  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      {
        handle: this.params.entityHandle,
        changeKind: "height",
        oldValue: String(this.params.fromHeight),
        newValue: String(this.params.toHeight),
      },
    ]);
    if (this.executed) return;
    this.apply(this.params.to);
    this.executed = true;
  }

  undo(): void {
    if (!this.executed) return;
    this.apply(this.params.from);
    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    if (this.executed) return;
    this.apply(this.params.to);
    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {
    // Nothing to clean up
  }

  private apply(state: DimensionSizeState): void {
    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;
    const entityId = findEntityByHandle(module, db, this.params.entityHandle);
    if (!entityId) return;

    applyDimensionSize(module, db, entityId, state);
    deleteObj(entityId);
    // The text changed size: so did the extents the selection grid is drawn on.
    selectionService.updateGrid();
  }
}
